import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatDate, STATUS_BADGE } from "@/lib/format";
import { parseKartuUcapan } from "@/lib/ucapanParser";
import { PeriodFilter } from "@/components/PeriodFilter";
import { usePersistentPeriod } from "@/lib/usePersistentPeriod";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Wallet, Printer, ShoppingBag, Coins, TrendingUp, Pencil, Search, XCircle } from "lucide-react";
import { DragonIcon } from "@/components/DragonIcon";
import { printInvoice, printCustomerCombinedInvoice, remainingOf } from "@/lib/invoice";

const today = () => new Date().toISOString().slice(0, 10);
const orderNo = (id) => `#${id.slice(-6).toUpperCase()}`;
const paidOf = (t) => Math.max((t.amount || 0) - remainingOf(t), 0);

const emptyForm = () => ({
  category: "", base_price: "", sell_price: "", quantity: "1", description: "",
  customer_kind: "baru", customer_id: null, customer_name: "", customer_phone: "",
  supplier_id: null, supplier_name: "", supplier_phone: "", supplier_tempo_id: "",
  down_payment: "", down_payment_account: "Bank BCA", cashback: "",
  customer_tempo: "0", customer_due_days: "0",
  payment_date: "", delivery_date: "",
  ucapan: "", dari: "", alamat: "",
  payment_method: "Bank BCA", payment_status: "Lunas", date: today(),
});

const REQUIRED_INPUT_CLASS = "border-amber-300 bg-amber-50/70 focus-visible:ring-amber-500 dark:border-amber-700 dark:bg-amber-950/20";

export default function Transactions() {
  const { user } = useAuth();
  const canManage = user.role === "owner" || user.role === "admin";
  const [period, setPeriod, range, setRange] = usePersistentPeriod("transactions");
  const [txs, setTxs] = useState([]);
  const [stats, setStats] = useState(null);
  const [opts, setOpts] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [supplierTempos, setSupplierTempos] = useState([]);
  const [custResults, setCustResults] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [orderProof, setOrderProof] = useState("");
  const [orderProofBusy, setOrderProofBusy] = useState(false);
  const [orderProofPreview, setOrderProofPreview] = useState("");

  const needsProof = form.payment_status !== "Belum Dibayar" || (parseFloat(form.down_payment) || 0) > 0;
  const priceInvalid = form.base_price !== "" && form.sell_price !== "" && (parseFloat(form.base_price) || 0) > (parseFloat(form.sell_price) || 0);

  const uploadOrderProof = async (file) => {
    if (!file) return;
    if (orderProofPreview) URL.revokeObjectURL(orderProofPreview);
    setOrderProofPreview(URL.createObjectURL(file));
    setOrderProofBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/payment-proof", fd);
      setOrderProof(r.data.path);
      toast.success("Bukti pembayaran terunggah");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal mengunggah bukti, coba lagi");
      setOrderProof("");
    } finally { setOrderProofBusy(false); }
  };

  const resetOrderProof = () => {
    if (orderProofPreview) URL.revokeObjectURL(orderProofPreview);
    setOrderProof(""); setOrderProofPreview(""); setOrderProofBusy(false);
  };

  const q = search.trim().toLowerCase();
  const filteredTxs = q
    ? txs.filter((t) =>
        (t.customer_name || "").toLowerCase().includes(q) ||
        (t.customer_phone || "").toLowerCase().includes(q) ||
        orderNo(t._id).toLowerCase().includes(q))
    : txs;

  const [payStatus, setPayStatus] = useState("all");
  const [adminF, setAdminF] = useState("all");
  const [adminOptions, setAdminOptions] = useState([]);
  useEffect(() => { api.get("/deliveries/admins").then((r) => setAdminOptions(r.data.admins || [])).catch(() => {}); }, []);
  const TX_TABS = [
    { id: "all", label: "Semua" },
    { id: "Lunas", label: "Lunas" },
    { id: "Sebagian", label: "Dibayar Sebagian" },
    { id: "Belum Dibayar", label: "Belum Dibayar" },
  ];
  const byPayStatus = (payStatus === "all" ? filteredTxs : filteredTxs.filter((t) => t.payment_status === payStatus))
    .filter((t) => adminF === "all" || t.created_by_name === adminF)
    .slice()
    .sort((a, b) => ({ "Belum Dibayar": 0, "Sebagian": 1 }[a.payment_status] ?? 2) - ({ "Belum Dibayar": 0, "Sebagian": 1 }[b.payment_status] ?? 2));

  const [selected, setSelected] = useState([]);
  const printable = (t) => t.payment_status !== "Lunas";
  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const printSelected = () => {
    const rows = byPayStatus.filter((t) => selected.includes(t._id) && printable(t));
    if (!rows.length) { toast.error("Pilih minimal satu order yang belum lunas"); return; }
    const custNames = [...new Set(rows.map((r) => r.customer_name).filter(Boolean))];
    printCustomerCombinedInvoice({
      admin: user?.name || "-",
      customer: custNames.length === 1 ? custNames[0] : `${custNames[0] || "Pelanggan"} dll`,
      phone: custNames.length === 1 ? (rows[0].customer_phone || "") : "",
      periodLabel: "Order pilihan",
      bills: rows,
    });
  };

  const qstr = () => period === "custom" ? `period=custom&start=${range.start}&end=${range.end}` : `period=${period}`;
  const load = () => {
    api.get(`/transactions?type=income&${qstr()}`).then((r) => setTxs(r.data));
    api.get(`/transactions/income-stats?${qstr()}`).then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, range.start, range.end]);
  useEffect(() => { api.get("/meta/options").then((r) => setOpts(r.data)); }, []);
  useEffect(() => { api.get("/users?role=supplier").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/supplier-tempos?only_active=true").then((r) => setSupplierTempos(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (form.customer_kind !== "lama" || !form.customer_name || form.customer_id) return;
    const t = setTimeout(() => {
      api.get(`/customers?search=${encodeURIComponent(form.customer_name)}`).then((r) => setCustResults(r.data));
    }, 300);
    return () => clearTimeout(t);
  }, [form.customer_name, form.customer_kind, form.customer_id]);

  const filteredSuppliers = suppliers
    .filter((s) => s.name.toLowerCase().includes((form.supplier_name || "").toLowerCase()))
    .slice(0, 8);

  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (submitting) return;
    if (!form.customer_name.trim()) { toast.error("Nama pelanggan wajib diisi"); return; }
    if (!form.supplier_name.trim()) { toast.error("Nama supplier wajib diisi"); return; }
    if (!form.category || !form.base_price) { toast.error("Produk/kategori dan harga modal wajib diisi"); return; }
    if (!form.sell_price) { toast.error("Harga jual wajib diisi"); return; }
    if (!form.date) { toast.error("Tanggal order wajib diisi"); return; }
    if (!form.delivery_date) { toast.error("Tanggal pengiriman wajib diisi"); return; }
    if (needsProof && !orderProof) { toast.error(orderProofBusy ? "Tunggu unggahan bukti selesai" : "Bukti gambar pembayaran wajib dilampirkan untuk order Lunas / Uang Muka"); return; }
    const qty = parseInt(form.quantity) || 1;
    const unitSell = parseFloat(form.sell_price) || 0;
    const unitBase = parseFloat(form.base_price) || 0;
    if (unitBase > unitSell) { toast.error("Harga modal tidak boleh melebihi harga jual"); return; }
    setSubmitting(true);
    try {
      await api.post("/transactions", {
        ...form,
        type: "income",
        amount: qty * unitSell,
        customer_name: form.customer_name || null,
        customer_phone: form.customer_phone || null,
        supplier_name: form.supplier_name || null,
        supplier_phone: form.supplier_phone || null,
        supplier_id: form.supplier_id || null,
        payment_date: form.payment_date || null,
        proof_image: orderProof || null,
        delivery_date: form.delivery_date || null,
        supplier_tempo_id: form.supplier_tempo_id || null,
        down_payment: form.down_payment ? parseFloat(form.down_payment) : 0,
        down_payment_account: form.down_payment_account,
        cashback: form.cashback ? parseFloat(form.cashback) : 0,
        customer_tempo: form.customer_tempo,
        customer_due_days: parseInt(form.customer_due_days) || 0,
        ucapan: form.ucapan || null,
        dari: form.dari || null,
        alamat: form.alamat || null,
        quantity: qty,
        unit_sell: unitSell,
        unit_base: unitBase,
        base_price: qty * unitBase,
        sell_price: qty * unitSell,
      });
      toast.success("Order berhasil dicatat");
      setOpen(false);
      setForm(emptyForm());
      resetOrderProof();
      setCustResults([]);
      api.get("/users?role=supplier").then((r) => setSuppliers(r.data)).catch(() => {});
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const [delTarget, setDelTarget] = useState(null);
  const [delImpact, setDelImpact] = useState(null);
  const [delReason, setDelReason] = useState("");
  const [delConfirm, setDelConfirm] = useState(false);
  const [delBusy, setDelBusy] = useState(false);

  const remove = async (id) => {
    const t = (txs || []).find((x) => x._id === id);
    setDelTarget(t || { _id: id });
    setDelImpact(null); setDelReason(""); setDelConfirm(false);
    try {
      const { data } = await api.get(`/transactions/${id}/delete-impact`);
      setDelImpact(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitDelete = async () => {
    if (!delConfirm) { toast.error("Centang konfirmasi terlebih dahulu"); return; }
    setDelBusy(true);
    try {
      const res = await api.post("/deletion-requests", { resource_type: "transaction", resource_id: delTarget._id, label: "order", reason: delReason });
      toast.success(res.data.pending ? "Permintaan hapus dikirim, menunggu persetujuan Owner" : "Order dihapus & saldo dikoreksi");
      setDelTarget(null); load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDelBusy(false); }
  };

  const [editFor, setEditFor] = useState(null);
  const [eform, setEform] = useState(null);
  const openEdit = (t) => {
    setEditFor(t);
    setEform({
      date: t.date || today(),
      category: t.category || "",
      customer_name: t.customer_name || "",
      customer_phone: t.customer_phone || "",
      quantity: String(t.quantity || 1),
      unit_sell: String(t.unit_sell != null ? t.unit_sell : t.amount || 0),
      unit_base: String(t.unit_base != null ? t.unit_base : (t.base_price || 0) / (t.quantity || 1)),
      cashback: String(t.cashback || 0),
      customer_due_date: t.customer_due_date || "",
      description: t.description || "",
      ucapan: t.ucapan || "",
      dari: t.dari || "",
      alamat: t.alamat || "",
    });
  };
  const submitEdit = async () => {
    if (!eform.category) { toast.error("Produk/kategori wajib diisi"); return; }
    const qty = parseInt(eform.quantity) || 1;
    const unitSell = parseFloat(eform.unit_sell) || 0;
    const unitBase = parseFloat(eform.unit_base) || 0;
    if (unitBase > unitSell) { toast.error("Harga modal tidak boleh melebihi harga jual"); return; }
    try {
      await api.patch(`/transactions/${editFor._id}`, {
        date: eform.date || null,
        category: eform.category,
        customer_name: eform.customer_name || null,
        customer_phone: eform.customer_phone || null,
        quantity: qty,
        unit_sell: unitSell,
        unit_base: unitBase,
        amount: qty * unitSell,
        base_price: qty * unitBase,
        sell_price: qty * unitSell,
        cashback: parseFloat(eform.cashback) || 0,
        customer_due_date: eform.customer_due_date || null,
        description: eform.description || null,
        ucapan: eform.ucapan || null,
        dari: eform.dari || null,
        alamat: eform.alamat || null,
      });
      toast.success("Order diperbarui");
      setEditFor(null); setEform(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", account: "Bank BCA", date: today() });
  const [payProof, setPayProof] = useState("");
  const [payProofBusy, setPayProofBusy] = useState(false);

  const uploadProof = async (file) => {
    if (!file) return;
    setPayProofBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/payment-proof", fd);
      setPayProof(r.data.path);
      toast.success("Bukti pembayaran terunggah");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal mengunggah bukti"); }
    finally { setPayProofBusy(false); }
  };

  const submitPay = async () => {
    if (!payForm.amount) { toast.error("Isi jumlah pembayaran"); return; }
    if (!payProof) { toast.error("Bukti gambar pembayaran wajib disertakan"); return; }
    try {
      await api.patch(`/transactions/${payFor._id}/pay`, { ...payForm, amount: parseFloat(payForm.amount), proof_image: payProof });
      toast.success("Pembayaran dicatat");
      setPayFor(null); setPayForm({ amount: "", account: "Bank BCA", date: today() }); setPayProof("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Pembatalan Order ----
  const isApprover = user.role === "owner" || user.can_approve_deletion;
  const [cancelFor, setCancelFor] = useState(null);
  const [cForm, setCForm] = useState(null);
  const [cProof, setCProof] = useState("");
  const [cProofBusy, setCProofBusy] = useState(false);
  const [targetOrders, setTargetOrders] = useState([]);

  const openCancel = (t) => {
    setCancelFor(t);
    setCProof("");
    setCForm({ money_action: "titipan", reason: "", refund_amount: String(paidOf(t)), refund_account: "Bank BCA", target_order_id: "", transfer_amount: String(paidOf(t)) });
    setTargetOrders([]);
    const cid = t.customer_id;
    const url = `/transactions?type=income&period=all`;
    api.get(url).then((r) => {
      const list = (r.data || []).filter((o) => o._id !== t._id && o.order_status !== "Dibatalkan" && remainingOf(o) > 0 &&
        ((cid && o.customer_id === cid) || (!cid && o.customer_name === t.customer_name)));
      setTargetOrders(list);
    }).catch(() => {});
  };

  const uploadCancelProof = async (file) => {
    if (!file) return;
    setCProofBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload/payment-proof", fd);
      setCProof(r.data.path);
      toast.success("Bukti refund terunggah");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal mengunggah bukti"); }
    finally { setCProofBusy(false); }
  };

  const submitCancel = async () => {
    const paid = paidOf(cancelFor);
    const body = { money_action: cForm.money_action, reason: cForm.reason || "" };
    if (cForm.money_action === "refund") {
      if (paid <= 0) { toast.error("Order ini belum ada pembayaran, tidak ada yang direfund"); return; }
      if (!cProof) { toast.error("Bukti refund wajib dilampirkan"); return; }
      body.refund_amount = parseFloat(cForm.refund_amount) || paid;
      body.refund_account = cForm.refund_account;
      body.refund_proof = cProof;
    }
    if (cForm.money_action === "transfer") {
      if (paid <= 0) { toast.error("Order ini belum ada pembayaran untuk dialihkan"); return; }
      if (!cForm.target_order_id) { toast.error("Pilih order tujuan"); return; }
      body.target_order_id = cForm.target_order_id;
      body.transfer_amount = parseFloat(cForm.transfer_amount) || paid;
    }
    try {
      const r = await api.post(`/transactions/${cancelFor._id}/request-cancel`, body);
      toast.success(r.data.cancelled ? "Order dibatalkan" : "Permintaan pembatalan dikirim, menunggu persetujuan Owner");
      setCancelFor(null); setCForm(null); setCProof("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const statCards = [
    { label: "Jumlah Order", value: stats ? stats.order_count : 0, icon: ShoppingBag, money: false, tone: ["bg-sky-100 dark:bg-sky-950", "text-sky-600"] },
    { label: "Total Omzet", value: stats ? stats.total_omzet : 0, icon: Coins, money: true, tone: ["bg-emerald-100 dark:bg-emerald-950", "text-emerald-600"] },
    { label: "Total Modal", value: stats ? stats.total_modal : 0, icon: DragonIcon, money: true, tone: ["bg-amber-100 dark:bg-amber-950", "text-amber-600"] },
    { label: "Laba Kotor", value: stats ? stats.gross_profit : 0, icon: TrendingUp, money: true, tone: ["bg-indigo-100 dark:bg-indigo-950", "text-indigo-600"] },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Transaksi Harian</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Pusat monitoring order masuk / penjualan. Pengeluaran dikelola di menu terpisah.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <PeriodFilter value={period} onChange={setPeriod} range={range} onRangeChange={setRange} />
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetOrderProof(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-transaction-btn" className="gap-1.5 shrink-0"><Plus className="h-4 w-4" /> Order Baru</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading">Catat Order Masuk</DialogTitle></DialogHeader>
              <div className="space-y-4 py-1">
                <div className="space-y-3 rounded-xl border border-border p-3">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Pelanggan</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[{ id: "baru", label: "Pelanggan Baru" }, { id: "lama", label: "Pelanggan Lama" }].map((k) => (
                      <button key={k.id} type="button"
                        onClick={() => setForm({ ...form, customer_kind: k.id, customer_id: null, customer_name: "", customer_phone: "" })}
                        data-testid={`customer-kind-${k.id}`}
                        className={`rounded-lg border-2 py-2 text-sm font-semibold transition-colors ${form.customer_kind === k.id ? "border-primary bg-accent text-accent-foreground" : "border-border text-muted-foreground"}`}>
                        {k.label}
                      </button>
                    ))}
                  </div>
                  <div className="relative space-y-1.5">
                    <Label>{form.customer_kind === "lama" ? "Cari Nama / No. Telepon" : "Nama Pelanggan"} <span className="text-rose-600">(wajib)</span></Label>
                    <Input data-testid="tx-customer-name" required className={REQUIRED_INPUT_CLASS} value={form.customer_name}
                      onChange={(e) => setForm({ ...form, customer_name: e.target.value, customer_id: null })}
                      placeholder={form.customer_kind === "lama" ? "Ketik nama atau nomor telepon..." : "Nama pelanggan baru"} />
                    {form.customer_kind === "lama" && custResults.length > 0 && !form.customer_id && (
                      <div data-testid="customer-results" className="absolute z-10 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg max-h-40 overflow-y-auto">
                        {custResults.map((c) => (
                          <button key={c._id} type="button" data-testid={`customer-pick-${c._id}`}
                            onClick={() => { setForm({ ...form, customer_id: c._id, customer_name: c.name, customer_phone: c.phone }); setCustResults([]); }}
                            className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent">
                            <span className="font-medium">{c.name}</span>
                            <span className="text-xs text-muted-foreground">{c.phone}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label>No. Telepon</Label>
                    <Input data-testid="tx-customer-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="08xx..." />
                  </div>
                </div>

                <div className="space-y-3 rounded-xl border border-dashed border-primary/50 p-3">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Supplier (tagihan otomatis)</p>
                  <div className="relative space-y-1.5">
                    <Label>Nama Supplier <span className="text-rose-600">(wajib)</span></Label>
                    <Input data-testid="tx-order-supplier-name" required className={REQUIRED_INPUT_CLASS} value={form.supplier_name}
                      onChange={(e) => setForm({ ...form, supplier_name: e.target.value, supplier_id: null })}
                      placeholder="Pilih supplier untuk tagihan otomatis..." />
                    {form.supplier_name && !form.supplier_id && filteredSuppliers.length > 0 && (
                      <div data-testid="order-supplier-results" className="absolute z-10 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg max-h-40 overflow-y-auto">
                        {filteredSuppliers.map((s) => (
                          <button key={s._id} type="button" data-testid={`order-supplier-pick-${s._id}`}
                            onClick={() => setForm({ ...form, supplier_id: s._id, supplier_name: s.name, supplier_phone: "" })}
                            className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-accent">
                            <span className="font-medium">{s.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {form.supplier_name && !form.supplier_id && (
                    <div className="space-y-1.5">
                      <Label>No. HP Supplier Baru</Label>
                      <Input data-testid="tx-order-supplier-phone" value={form.supplier_phone}
                        onChange={(e) => setForm({ ...form, supplier_phone: e.target.value })}
                        placeholder="08xx..." />
                    </div>
                  )}
                  <p className="text-[11px] text-muted-foreground">Saat order disimpan, tagihan ke supplier otomatis dibuat (sebesar harga modal) dan tercatat atas nama Anda. Supplier baru otomatis terdaftar di Data Vendor; username & password akunnya diatur Owner.</p>
                </div>

                <div className="space-y-1.5">
                  <Label>Produk / Jasa (Kategori) <span className="text-rose-600">(wajib)</span></Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="tx-category" aria-required="true" className={REQUIRED_INPUT_CLASS}><SelectValue placeholder="Pilih produk/jasa" /></SelectTrigger>
                    <SelectContent>{(opts?.income_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label>Jumlah</Label>
                    <Input data-testid="tx-quantity" type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="1" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Harga Modal /pcs <span className="text-rose-600">(wajib)</span></Label>
                    <Input data-testid="tx-base-price" required className={REQUIRED_INPUT_CLASS} type="number" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} placeholder="0" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Harga Jual /pcs <span className="text-rose-600">(wajib)</span></Label>
                    <Input data-testid="tx-sell-price" required className={REQUIRED_INPUT_CLASS} type="number" value={form.sell_price} onChange={(e) => setForm({ ...form, sell_price: e.target.value })} placeholder="0" />
                  </div>
                </div>
                {priceInvalid && <p data-testid="tx-price-warning" className="text-[11px] font-medium text-rose-600">Harga modal tidak boleh melebihi harga jual.</p>}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Metode</Label>
                    <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                      <SelectTrigger data-testid="tx-method"><SelectValue /></SelectTrigger>
                      <SelectContent>{opts?.payment_methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Status Pembayaran</Label>
                    <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                      <SelectTrigger data-testid="tx-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{opts?.payment_status.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                {needsProof && (
                  <div className="space-y-1.5 rounded-xl border border-amber-300 bg-amber-50/50 dark:bg-amber-950/20 p-3" data-testid="tx-order-proof-block">
                    <Label>Bukti Gambar Pembayaran <span className="text-rose-600">(wajib)</span></Label>
                    <Input data-testid="tx-order-proof" required type="file" accept="image/jpeg,image/png,image/webp" className={`text-xs ${REQUIRED_INPUT_CLASS}`} onChange={(e) => uploadOrderProof(e.target.files?.[0])} />
                    {orderProofBusy && <p className="text-[11px] text-muted-foreground">Mengunggah bukti...</p>}
                    {orderProof && !orderProofBusy && <p className="text-[11px] text-emerald-600 font-medium" data-testid="tx-order-proof-ok">Bukti terlampir ✓</p>}
                    {!orderProof && !orderProofBusy && <p className="text-[11px] text-rose-600">Order Lunas / Uang Muka wajib melampirkan bukti. Tanpa bukti, order tidak bisa disimpan.</p>}
                    {orderProofPreview && (
                      <img src={orderProofPreview} alt="Pratinjau bukti pembayaran" data-testid="tx-order-proof-preview" className="mt-1 max-h-40 rounded-lg border border-border object-contain" />
                    )}
                  </div>
                )}

                <div className="space-y-3 rounded-xl border border-border p-3">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Uang Muka, Cashback & Tempo Pelanggan</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Uang Muka (Rp)</Label>
                      <Input data-testid="tx-down-payment" type="number" value={form.down_payment} onChange={(e) => setForm({ ...form, down_payment: e.target.value })} placeholder="0" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Rekening Uang Muka</Label>
                      <Select value={form.down_payment_account} onValueChange={(v) => setForm({ ...form, down_payment_account: v })}>
                        <SelectTrigger data-testid="tx-dp-account"><SelectValue /></SelectTrigger>
                        <SelectContent>{opts?.payment_methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Cashback (Rp) — dicairkan setelah lunas</Label>
                    <Input data-testid="tx-cashback" type="number" value={form.cashback} onChange={(e) => setForm({ ...form, cashback: e.target.value })} placeholder="0" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Tempo Tagihan Pelanggan</Label>
                    <Select value={form.customer_tempo} onValueChange={(v) => setForm({ ...form, customer_tempo: v, customer_due_days: v === "custom" ? form.customer_due_days : v })}>
                      <SelectTrigger data-testid="tx-customer-tempo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0 Hari / Cash</SelectItem>
                        <SelectItem value="1">1 Hari</SelectItem>
                        <SelectItem value="3">3 Hari</SelectItem>
                        <SelectItem value="7">7 Hari</SelectItem>
                        <SelectItem value="14">14 Hari</SelectItem>
                        <SelectItem value="30">30 Hari</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                    {form.customer_tempo === "custom" && (
                      <Input data-testid="tx-customer-due-custom" type="number" className="mt-2" value={form.customer_due_days} onChange={(e) => setForm({ ...form, customer_due_days: e.target.value })} placeholder="Jumlah hari" />
                    )}
                  </div>
                  {(parseFloat(form.sell_price) || 0) > 0 && (
                    <div className="rounded-lg bg-accent/50 p-2.5 text-xs space-y-0.5" data-testid="tx-billing-preview">
                      <div className="flex justify-between"><span className="text-muted-foreground">Harga Jual × Jumlah</span><span>{formatIDR(parseFloat(form.sell_price) || 0)} × {parseInt(form.quantity) || 1}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Total Harga Jual</span><span>{formatIDR((parseFloat(form.sell_price) || 0) * (parseInt(form.quantity) || 1))}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Total Harga Modal</span><span>- {formatIDR((parseFloat(form.base_price) || 0) * (parseInt(form.quantity) || 1))}</span></div>
                      <div className="flex justify-between font-semibold border-t border-border pt-1 mt-1"><span>Laba Kotor</span><span className="text-indigo-600">{formatIDR(((parseFloat(form.sell_price) || 0) - (parseFloat(form.base_price) || 0)) * (parseInt(form.quantity) || 1))}</span></div>
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label>Tanggal Order <span className="text-rose-600">(wajib)</span></Label>
                  <Input data-testid="tx-date" required className={REQUIRED_INPUT_CLASS} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Tanggal Pembayaran</Label>
                    <Input data-testid="tx-payment-date" type="date" className="w-full min-w-0" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Tanggal Pengiriman <span className="text-rose-600">(wajib)</span></Label>
                    <Input data-testid="tx-delivery-date" required className={`${REQUIRED_INPUT_CLASS} w-full min-w-0`} type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Tempo Tagihan Supplier</Label>
                  <Select value={form.supplier_tempo_id} onValueChange={(v) => setForm({ ...form, supplier_tempo_id: v })}>
                    <SelectTrigger data-testid="tx-supplier-tempo"><SelectValue placeholder="Pilih tempo supplier" /></SelectTrigger>
                    <SelectContent>
                      {supplierTempos.map((t) => <SelectItem key={t._id} value={t._id}>{t.name} ({t.value} {t.unit})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">Dipakai untuk menghitung jatuh tempo tagihan supplier otomatis.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Kartu Ucapan</Label>
                  <Textarea data-testid="tx-description" value={form.description} onChange={(e) => { const v = e.target.value; const p = parseKartuUcapan(v); setForm({ ...form, description: v, ucapan: p.ucapan, dari: p.dari, alamat: p.alamat }); }}
                    placeholder={"Tulis pesan bebas, contoh:\nSelamat & Sukses atas pembukaan usaha baru\nDari: PT Maju Bersama\nAlamat: Jl. Merdeka No. 12, Bandung"} rows={3} />
                  <p className="text-[11px] text-muted-foreground">Teks otomatis dibaca & dipisah menjadi <b>Ucapan</b>, <b>Dari</b>, dan <b>Alamat</b>.</p>
                </div>
                {(form.ucapan || form.dari || form.alamat) && (
                  <div className="space-y-2.5 rounded-lg border border-primary/40 bg-accent/40 p-3" data-testid="ucapan-preview">
                    <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Hasil baca otomatis — silakan koreksi bila perlu</p>
                    <div className="space-y-1.5"><Label className="text-xs">Ucapan</Label><Textarea data-testid="tx-ucapan" rows={6} className="min-h-[120px] text-xs leading-relaxed" value={form.ucapan} onChange={(e) => setForm({ ...form, ucapan: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Dari</Label><Textarea data-testid="tx-dari" rows={4} className="min-h-[80px] text-xs leading-relaxed" value={form.dari} onChange={(e) => setForm({ ...form, dari: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Alamat</Label><Textarea data-testid="tx-alamat" rows={4} className="min-h-[80px] text-xs leading-relaxed" value={form.alamat} onChange={(e) => setForm({ ...form, alamat: e.target.value })} /></div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
                <Button onClick={submit} disabled={submitting || (needsProof && (!orderProof || orderProofBusy)) || priceInvalid} data-testid="tx-submit" variant="gold">{submitting ? "Menyimpan..." : "Simpan Order"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="income-stats">
        {statCards.map((s) => (
          <Card key={s.label} data-testid={`stat-${s.label}`} className="p-5">
            <div className="flex items-center gap-3">
              <div className={`grid place-items-center h-11 w-11 rounded-xl ${s.tone[0]}`}><s.icon className={`h-5 w-5 ${s.tone[1]}`} /></div>
              <div><p className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</p>
                <p className="font-heading text-lg font-extrabold">{s.money ? formatIDR(s.value) : s.value}</p></div>
            </div>
          </Card>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="tx-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari no. order atau nama pelanggan..." className="pl-9" />
        </div>
        <Select value={adminF} onValueChange={setAdminF}>
          <SelectTrigger className="w-full sm:w-48" data-testid="tx-admin-filter">
            <SelectValue placeholder="Semua Admin" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Admin</SelectItem>
            {adminOptions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="tx-status-tabs">
        {TX_TABS.map((t) => {
          const rows = t.id === "all" ? filteredTxs : filteredTxs.filter((x) => x.payment_status === t.id);
          const amt = rows.reduce((a, x) => a + (x.amount || 0), 0);
          return (
            <button key={t.id} type="button" data-testid={`tx-tab-${t.id}`} onClick={() => setPayStatus(t.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${payStatus === t.id ? "bg-[#0A1633] text-white border-[#0A1633]" : "bg-card text-muted-foreground border-border hover:border-[#C9A227]/60"}`}>
              {t.label} · {rows.length} · {formatIDR(amt)}
            </button>
          );
        })}
      </div>

      {selected.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="tx-print-selected-bar">
          <Button size="sm" variant="gold" className="h-8 text-xs gap-1" onClick={printSelected} data-testid="tx-print-selected">
            <Printer className="h-3.5 w-3.5" /> Cetak Invoice Terpilih ({selected.length} order)
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setSelected([])} data-testid="tx-clear-selected">Bersihkan Pilihan</Button>
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="hidden lg:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead className="whitespace-nowrap">Tgl Order</TableHead>
                <TableHead className="whitespace-nowrap">No. Order</TableHead>
                <TableHead>Pelanggan</TableHead>
                <TableHead>Status</TableHead>
                {canManage && <TableHead>Admin</TableHead>}
                {canManage && <TableHead data-testid="tx-header-supplier">Supplier</TableHead>}
                <TableHead>Produk</TableHead>
                <TableHead className="text-right">Jumlah</TableHead>
                <TableHead className="text-right whitespace-nowrap">Harga Jual</TableHead>
                <TableHead className="text-right whitespace-nowrap">Harga Modal</TableHead>
                <TableHead className="text-right">Cashback</TableHead>
                <TableHead className="text-right whitespace-nowrap">Total Order</TableHead>
                <TableHead className="text-right whitespace-nowrap">Laba Kotor</TableHead>
                <TableHead>Bayar</TableHead>
                <TableHead>Status Order</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {byPayStatus.length === 0 && (
                <TableRow><TableCell colSpan={canManage ? 17 : 15} className="text-center text-muted-foreground py-10">{q ? "Tidak ada order yang cocok dengan pencarian." : "Belum ada order pada periode ini."}</TableCell></TableRow>
              )}
              {byPayStatus.map((t) => (
                <TableRow key={t._id} data-testid={`tx-row-${t._id}`}>
                  <TableCell className="w-8">
                    {printable(t) && (
                      <input type="checkbox" data-testid={`tx-select-${t._id}`} checked={selected.includes(t._id)} onChange={() => toggleSel(t._id)} className="h-4 w-4 accent-[#0A1633] cursor-pointer" />
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{formatDate(t.date)}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm font-medium">{orderNo(t._id)}</TableCell>
                  <TableCell>
                    <p className="text-sm font-medium">{t.customer_name || "-"}</p>
                    {t.customer_phone && <p className="text-xs text-muted-foreground">{t.customer_phone}</p>}
                  </TableCell>
                  <TableCell>
                    {t.customer_status ? (
                      <Badge variant="outline" className={t.customer_status === "Baru"
                        ? "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300"
                        : "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-950 dark:text-sky-300"}>{t.customer_status}</Badge>
                    ) : <span className="text-xs text-muted-foreground">-</span>}
                  </TableCell>
                  {canManage && <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{t.created_by_name}</TableCell>}
                  {canManage && (
                    <TableCell className="text-sm text-muted-foreground">
                      <span data-testid={`tx-supplier-${t._id}`} title={t.supplier_name || ""} className="block max-w-[150px] truncate">{t.supplier_name || "-"}</span>
                      {t.supplier_settled === "Lunas" && <span data-testid={`tx-supplier-lunas-${t._id}`} className="inline-block mt-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 text-[10px] font-semibold px-1.5 py-0.5">Lunas</span>}
                    </TableCell>
                  )}
                  <TableCell className="text-sm">{t.category}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">{t.quantity || 1}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">{formatIDR(t.unit_sell != null ? t.unit_sell : t.amount)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap text-muted-foreground">{formatIDR(t.base_price || 0)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap text-amber-600">{(t.cashback || 0) > 0 ? formatIDR(t.cashback) : "-"}</TableCell>
                  <TableCell className="text-right whitespace-nowrap font-semibold">{formatIDR(t.amount)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap font-semibold text-indigo-600">{formatIDR((t.amount || 0) - (t.base_price || 0))}</TableCell>
                  <TableCell>
                    {t.order_status === "Dibatalkan" ? (
                      <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-300 dark:bg-slate-900 dark:text-slate-400">Dibatalkan</Badge>
                    ) : (<>
                      <Badge variant="outline" className={STATUS_BADGE[t.payment_status]}>{t.payment_status}</Badge>
                      {remainingOf(t) > 0 && <span className="block text-[10px] text-rose-500 mt-0.5 whitespace-nowrap">Sisa: {formatIDR(remainingOf(t))}</span>}
                    </>)}
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap text-muted-foreground">
                    {t.order_status === "Dibatalkan"
                      ? <Badge variant="outline" className="bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-950 dark:text-rose-300" data-testid={`tx-cancelled-${t._id}`}>Dibatalkan</Badge>
                      : (t.pending_cancellation ? <span className="text-[10px] font-semibold text-amber-600">Menunggu persetujuan batal</span> : (t.delivery_status || "-"))}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {t.order_status === "Dibatalkan" ? (
                        <span className="text-[10px] text-muted-foreground px-1">—</span>
                      ) : (<>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => printInvoice(t)} data-testid={`tx-invoice-${t._id}`} title="Cetak Invoice">
                        <Printer className="h-4 w-4" />
                      </Button>
                      {canManage && remainingOf(t) > 0 && (
                        <Button variant="outline" size="sm" className="h-8 gap-1" onClick={() => { setPayProof(""); setPayFor(t); setPayForm({ amount: String(remainingOf(t)), account: "Bank BCA", date: today() }); }} data-testid={`tx-pay-${t._id}`}>
                          <Wallet className="h-3.5 w-3.5" /> Bayar
                        </Button>
                      )}
                      {canManage && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => openEdit(t)} data-testid={`tx-edit-${t._id}`} title="Edit Order">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {canManage && !t.pending_cancellation && (
                        <Button variant="ghost" size="sm" className="h-8 gap-1 px-2 text-xs text-muted-foreground hover:text-rose-600" onClick={() => openCancel(t)} data-testid={`tx-cancel-${t._id}`} title="Batalkan Order">
                          <XCircle className="h-4 w-4" /> Batalkan
                        </Button>
                      )}
                      {canManage && (t.pending_deletion ? (
                        <span data-testid={`tx-pending-${t._id}`} className="text-[9px] font-semibold text-amber-600 px-1">Menunggu persetujuan</span>
                      ) : (
                        <Button variant="ghost" size="sm" className="h-8 gap-1 px-2 text-xs text-muted-foreground hover:text-destructive" onClick={() => remove(t._id)} data-testid={`tx-delete-${t._id}`} title="Hapus Order">
                          <Trash2 className="h-4 w-4" /> Hapus
                        </Button>
                      ))}
                      </>)}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="lg:hidden divide-y divide-border">
          {byPayStatus.length === 0 && <p className="text-center text-muted-foreground text-xs py-8">{q ? "Tidak ada order yang cocok dengan pencarian." : "Belum ada order pada periode ini."}</p>}
          {byPayStatus.map((t) => (
            <div key={t._id} data-testid={`tx-row-${t._id}`} className="p-3 space-y-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{orderNo(t._id)} · {t.category}</p>
                  {t.supplier_name && <p data-testid={`tx-supplier-mobile-${t._id}`} className="text-[10px] text-muted-foreground break-words">Supplier: {t.supplier_name}{t.supplier_settled === "Lunas" && <span data-testid={`tx-supplier-lunas-mobile-${t._id}`} className="ml-1 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 text-[9px] font-semibold px-1 py-0.5">Lunas</span>}</p>}
                  <p className="text-[11px] text-muted-foreground">{formatDate(t.date)}{canManage && t.created_by_name ? ` · ${t.created_by_name}` : ""}</p>
                </div>
                <p className="text-sm font-bold whitespace-nowrap">{formatIDR(t.amount)}</p>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="font-medium">{t.customer_name || "-"}</span>
                {t.customer_status && (
                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${t.customer_status === "Baru" ? "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300" : "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-950 dark:text-sky-300"}`}>{t.customer_status}</Badge>
                )}
                {t.customer_phone && <span className="text-[10px] text-muted-foreground">{t.customer_phone}</span>}
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                <div className="flex justify-between"><span className="text-muted-foreground">Jumlah</span><span>{t.quantity || 1}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Harga Jual</span><span>{formatIDR(t.unit_sell != null ? t.unit_sell : t.amount)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Harga Modal</span><span>{formatIDR(t.base_price || 0)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Cashback</span><span className="text-amber-600">{(t.cashback || 0) > 0 ? formatIDR(t.cashback) : "-"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Order</span><span className="font-semibold">{formatIDR(t.amount)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Laba Kotor</span><span className="font-semibold text-indigo-600">{formatIDR((t.amount || 0) - (t.base_price || 0))}</span></div>
                <div className="flex justify-between items-center"><span className="text-muted-foreground">Bayar</span><Badge variant="outline" className={`${t.order_status === "Dibatalkan" ? "bg-slate-100 text-slate-500 border-slate-300 dark:bg-slate-900 dark:text-slate-400" : STATUS_BADGE[t.payment_status]} text-[10px] px-1.5 py-0`}>{t.order_status === "Dibatalkan" ? "Dibatalkan" : t.payment_status}</Badge></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Status Order</span><span>{t.order_status === "Dibatalkan" ? <span className="text-rose-600 font-semibold" data-testid={`tx-cancelled-mobile-${t._id}`}>Dibatalkan</span> : (t.delivery_status || "-")}</span></div>
              </div>
              {remainingOf(t) > 0 && t.order_status !== "Dibatalkan" && <p className="text-[10px] text-rose-500">Sisa tagihan: {formatIDR(remainingOf(t))}</p>}
              {t.order_status === "Dibatalkan" && t.cancel_reason && <p className="text-[10px] text-muted-foreground">Alasan batal: {t.cancel_reason}</p>}
              {t.pending_cancellation && <p className="text-[10px] font-semibold text-amber-600">Menunggu persetujuan pembatalan</p>}
              {t.order_status !== "Dibatalkan" && (
              <div className="flex justify-end gap-1 pt-0.5">
                {printable(t) && (
                  <label className="flex items-center gap-1 mr-auto text-[10px] text-muted-foreground cursor-pointer" data-testid={`tx-select-mobile-${t._id}`}>
                    <input type="checkbox" checked={selected.includes(t._id)} onChange={() => toggleSel(t._id)} className="h-4 w-4 accent-[#0A1633]" />
                    Pilih utk invoice
                  </label>
                )}
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1 px-2" onClick={() => printInvoice(t)} data-testid={`tx-invoice-${t._id}`}>
                  <Printer className="h-3 w-3" /> Invoice
                </Button>
                {canManage && remainingOf(t) > 0 && (
                  <Button variant="outline" size="sm" className="h-7 text-xs gap-1 px-2" onClick={() => { setPayProof(""); setPayFor(t); setPayForm({ amount: String(remainingOf(t)), account: "Bank BCA", date: today() }); }} data-testid={`tx-pay-${t._id}`}>
                    <Wallet className="h-3 w-3" /> Bayar
                  </Button>
                )}
                {canManage && (
                  <Button variant="outline" size="sm" className="h-7 text-xs gap-1 px-2" onClick={() => openEdit(t)} data-testid={`tx-edit-${t._id}`}>
                    <Pencil className="h-3 w-3" /> Edit
                  </Button>
                )}
                {canManage && !t.pending_cancellation && (
                  <Button variant="outline" size="sm" className="h-7 text-xs gap-1 px-2 text-rose-600" onClick={() => openCancel(t)} data-testid={`tx-cancel-${t._id}`}>
                    <XCircle className="h-3 w-3" /> Batalkan
                  </Button>
                )}
                {canManage && (t.pending_deletion ? (
                  <span data-testid={`tx-pending-mobile-${t._id}`} className="text-[10px] font-semibold text-amber-600 self-center px-1">Menunggu persetujuan hapus</span>
                ) : (
                  <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 px-2 text-rose-600 hover:text-rose-700" onClick={() => remove(t._id)} data-testid={`tx-delete-${t._id}`}>
                    <Trash2 className="h-3 w-3" /> Hapus
                  </Button>
                ))}
              </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Order {editFor ? orderNo(editFor._id) : ""}</DialogTitle>
          </DialogHeader>
          {editFor && eform && (
            <div className="space-y-3 py-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Tanggal Order</Label><Input data-testid="tx-edit-date" type="date" className="h-9 text-xs" value={eform.date} onChange={(e) => setEform({ ...eform, date: e.target.value })} /></div>
                <div className="space-y-1">
                  <Label className="text-xs">Produk / Kategori</Label>
                  <Select value={eform.category} onValueChange={(v) => setEform({ ...eform, category: v })}>
                    <SelectTrigger data-testid="tx-edit-category" className="h-9 text-xs"><SelectValue placeholder="Pilih produk" /></SelectTrigger>
                    <SelectContent>{(opts?.income_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Nama Pelanggan</Label><Input data-testid="tx-edit-customer" className="h-9 text-xs" value={eform.customer_name} onChange={(e) => setEform({ ...eform, customer_name: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">No. Telepon</Label><Input data-testid="tx-edit-phone" className="h-9 text-xs" value={eform.customer_phone} onChange={(e) => setEform({ ...eform, customer_phone: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1"><Label className="text-xs">Jumlah</Label><Input data-testid="tx-edit-qty" type="number" min="1" className="h-9 text-xs" value={eform.quantity} onChange={(e) => setEform({ ...eform, quantity: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">Modal /pcs</Label><Input data-testid="tx-edit-base" type="number" className="h-9 text-xs" value={eform.unit_base} onChange={(e) => setEform({ ...eform, unit_base: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">Jual /pcs</Label><Input data-testid="tx-edit-sell" type="number" className="h-9 text-xs" value={eform.unit_sell} onChange={(e) => setEform({ ...eform, unit_sell: e.target.value })} /></div>
              </div>
              {(parseFloat(eform.unit_base) || 0) > (parseFloat(eform.unit_sell) || 0) && <p data-testid="tx-edit-price-warning" className="text-[11px] font-medium text-rose-600">Harga modal tidak boleh melebihi harga jual.</p>}
              <div className="rounded-lg bg-accent/50 p-2.5 text-xs flex justify-between" data-testid="tx-edit-preview">
                <span className="text-muted-foreground">Total Order Baru</span>
                <span className="font-semibold">{formatIDR((parseFloat(eform.unit_sell) || 0) * (parseInt(eform.quantity) || 1))}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Cashback (Rp)</Label><Input data-testid="tx-edit-cashback" type="number" className="h-9 text-xs" value={eform.cashback} onChange={(e) => setEform({ ...eform, cashback: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">Jatuh Tempo</Label><Input data-testid="tx-edit-due" type="date" className="h-9 text-xs" value={eform.customer_due_date} onChange={(e) => setEform({ ...eform, customer_due_date: e.target.value })} /></div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Kartu Ucapan</Label>
                <Textarea data-testid="tx-edit-description" rows={3} value={eform.description} onChange={(e) => { const v = e.target.value; const p = parseKartuUcapan(v); setEform({ ...eform, description: v, ucapan: p.ucapan, dari: p.dari, alamat: p.alamat }); }} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditFor(null)}>Batal</Button>
            <Button onClick={submitEdit} disabled={(parseFloat(eform?.unit_base) || 0) > (parseFloat(eform?.unit_sell) || 0)} data-testid="tx-edit-submit" variant="gold">Simpan Perubahan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Catat Pembayaran Pelanggan</DialogTitle></DialogHeader>
          {payFor && (
            <div className="space-y-3 py-1">
              <p className="text-sm text-muted-foreground">{payFor.customer_name || "Pelanggan"} — sisa <b className="text-foreground">{formatIDR(remainingOf(payFor))}</b></p>
              <div className="space-y-1.5"><Label>Jumlah Pembayaran (Rp)</Label><Input data-testid="pay-amount" type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Rekening Tujuan</Label>
                <Select value={payForm.account} onValueChange={(v) => setPayForm({ ...payForm, account: v })}>
                  <SelectTrigger data-testid="pay-account"><SelectValue /></SelectTrigger>
                  <SelectContent>{opts?.payment_methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Tanggal</Label><Input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Bukti Gambar Pembayaran <span className="text-rose-600">(wajib)</span></Label>
                <Input data-testid="pay-proof" type="file" accept="image/*" className="text-xs" onChange={(e) => uploadProof(e.target.files?.[0])} />
                {payProofBusy && <p className="text-[11px] text-muted-foreground">Mengunggah bukti...</p>}
                {payProof && !payProofBusy && <p className="text-[11px] text-emerald-600 font-medium" data-testid="pay-proof-ok">Bukti terlampir ✓</p>}
                {!payProof && !payProofBusy && <p className="text-[11px] text-rose-600">Tanpa bukti gambar, pembayaran tidak bisa disimpan.</p>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayFor(null)}>Batal</Button>
            <Button onClick={submitPay} disabled={!payProof || payProofBusy} data-testid="pay-submit" variant="gold">Simpan Pembayaran</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelFor} onOpenChange={(o) => !o && setCancelFor(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Batalkan Order {cancelFor ? orderNo(cancelFor._id) : ""}</DialogTitle></DialogHeader>
          {cancelFor && cForm && (
            <div className="space-y-3 py-1">
              <div className="rounded-lg bg-accent/50 p-2.5 text-xs space-y-0.5">
                <div className="flex justify-between"><span className="text-muted-foreground">Pelanggan</span><span className="font-medium">{cancelFor.customer_name || "-"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Order</span><span>{formatIDR(cancelFor.amount)}</span></div>
                <div className="flex justify-between font-semibold"><span>Uang Sudah Dibayar</span><span className="text-emerald-600" data-testid="cancel-paid">{formatIDR(paidOf(cancelFor))}</span></div>
              </div>
              {!isApprover && <p className="text-[11px] text-amber-600 font-medium">Pembatalan akan dikirim ke Owner untuk disetujui terlebih dahulu.</p>}
              <div className="space-y-1.5">
                <Label>Perlakuan Uang Pelanggan</Label>
                <div className="grid grid-cols-1 gap-1.5">
                  {[
                    { id: "titipan", label: "Jadikan Titipan (simpan sebagai saldo pelanggan)" },
                    { id: "refund", label: "Kembalikan Uang (refund tunai/transfer)" },
                    { id: "transfer", label: "Alihkan ke Order Lain pelanggan ini" },
                  ].map((o) => (
                    <button key={o.id} type="button" data-testid={`cancel-action-${o.id}`}
                      onClick={() => setCForm({ ...cForm, money_action: o.id })}
                      className={`rounded-lg border-2 px-3 py-2 text-left text-xs font-medium transition-colors ${cForm.money_action === o.id ? "border-primary bg-accent text-accent-foreground" : "border-border text-muted-foreground"}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
                {paidOf(cancelFor) <= 0 && <p className="text-[11px] text-muted-foreground">Order belum ada pembayaran — pembatalan hanya membatalkan order (tanpa titipan/refund).</p>}
              </div>

              {cForm.money_action === "refund" && paidOf(cancelFor) > 0 && (
                <div className="space-y-2 rounded-xl border border-amber-300 bg-amber-50/50 dark:bg-amber-950/20 p-3">
                  <div className="space-y-1.5"><Label>Nominal Refund (Rp)</Label><Input data-testid="cancel-refund-amount" type="number" value={cForm.refund_amount} onChange={(e) => setCForm({ ...cForm, refund_amount: e.target.value })} /></div>
                  <div className="space-y-1.5">
                    <Label>Dari Rekening</Label>
                    <Select value={cForm.refund_account} onValueChange={(v) => setCForm({ ...cForm, refund_account: v })}>
                      <SelectTrigger data-testid="cancel-refund-account"><SelectValue /></SelectTrigger>
                      <SelectContent>{opts?.payment_methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Bukti Refund <span className="text-rose-600">(wajib)</span></Label>
                    <Input data-testid="cancel-refund-proof" type="file" accept="image/*" className="text-xs" onChange={(e) => uploadCancelProof(e.target.files?.[0])} />
                    {cProofBusy && <p className="text-[11px] text-muted-foreground">Mengunggah...</p>}
                    {cProof && !cProofBusy && <p className="text-[11px] text-emerald-600 font-medium" data-testid="cancel-refund-proof-ok">Bukti terlampir ✓</p>}
                  </div>
                  <p className="text-[11px] text-muted-foreground">Refund mengurangi saldo kas/bank. Order batal tidak dihitung sebagai omzet.</p>
                </div>
              )}

              {cForm.money_action === "transfer" && paidOf(cancelFor) > 0 && (
                <div className="space-y-2 rounded-xl border border-sky-300 bg-sky-50/50 dark:bg-sky-950/20 p-3">
                  <div className="space-y-1.5">
                    <Label>Order Tujuan</Label>
                    <Select value={cForm.target_order_id} onValueChange={(v) => setCForm({ ...cForm, target_order_id: v })}>
                      <SelectTrigger data-testid="cancel-transfer-target"><SelectValue placeholder="Pilih order belum lunas pelanggan ini" /></SelectTrigger>
                      <SelectContent>
                        {targetOrders.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Tidak ada order aktif belum lunas.</div>}
                        {targetOrders.map((o) => <SelectItem key={o._id} value={o._id}>{orderNo(o._id)} · {o.category} · sisa {formatIDR(remainingOf(o))}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Nominal Dialihkan (Rp)</Label><Input data-testid="cancel-transfer-amount" type="number" value={cForm.transfer_amount} onChange={(e) => setCForm({ ...cForm, transfer_amount: e.target.value })} /></div>
                  <p className="text-[11px] text-muted-foreground">Uang dipindah sebagai pembayaran order tujuan — tidak menambah kas/bank lagi.</p>
                </div>
              )}

              <div className="space-y-1.5"><Label>Alasan Pembatalan</Label><Textarea data-testid="cancel-reason" rows={2} value={cForm.reason} onChange={(e) => setCForm({ ...cForm, reason: e.target.value })} placeholder="Opsional" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelFor(null)}>Tutup</Button>
            <Button onClick={submitCancel} disabled={cForm?.money_action === "refund" && (!cProof || cProofBusy)} data-testid="cancel-submit" variant="gold">
              {isApprover ? "Batalkan Order" : "Kirim Permintaan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-rose-600">Hapus Order {delImpact ? delImpact.order_no : ""}</DialogTitle>
            <DialogDescription className="text-xs">Hapus total untuk koreksi kesalahan input / order ganda. Seluruh data terkait dikoreksi dan saldo kas/bank dihitung ulang. Berbeda dari "Batalkan" yang menyimpan order sebagai Dibatalkan.</DialogDescription>
          </DialogHeader>
          {delImpact ? (
            <div className="space-y-3 py-1">
              <div className="rounded-lg bg-accent/50 p-2.5 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Pelanggan</span><span className="font-medium">{delImpact.customer_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Order</span><span>{formatIDR(delImpact.amount)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Modal / HPP</span><span>{formatIDR(delImpact.base_price)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Dibayar Pelanggan</span><span className="font-semibold text-emerald-600">{formatIDR(delImpact.customer_paid)}</span></div>
                {delImpact.supplier_paid > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Dibayar ke Supplier</span><span>{formatIDR(delImpact.supplier_paid)}</span></div>}
                {delImpact.cashback > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Cashback</span><span>{formatIDR(delImpact.cashback)} ({delImpact.cashback_status || "-"})</span></div>}
                {delImpact.supplier_bills > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Tagihan Supplier</span><span>{delImpact.supplier_bills} dihapus</span></div>}
                {delImpact.deposits > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Titipan dari order ini</span><span>{delImpact.deposits} dihapus</span></div>}
                {delImpact.affected_accounts.length > 0 && (
                  <div className="flex justify-between gap-2"><span className="text-muted-foreground">Rekening Terdampak</span><span className="text-right">{delImpact.affected_accounts.join(", ")}</span></div>
                )}
              </div>
              {(delImpact.customer_paid > 0 || delImpact.supplier_paid > 0) && (
                <div className="rounded-lg border border-rose-300 bg-rose-50/60 dark:bg-rose-950/20 p-2.5 text-[11px] text-rose-700 dark:text-rose-300" data-testid="del-money-warning">
                  Order ini sudah melibatkan uang nyata. Menghapus akan <b>membalik</b> pengaruhnya pada saldo kas/bank (koreksi salah input). Jika uang benar-benar diterima & tetap hak pelanggan, gunakan <b>Batalkan</b> (jadi titipan) — bukan Hapus.
                </div>
              )}
              <div className="rounded-lg border border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 p-2.5 text-[11px] text-amber-800 dark:text-amber-200">
                Seluruh data terkait order akan dikoreksi & dihapus dari data operasional. Catatan audit tetap disimpan.
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Alasan Hapus</Label><Textarea data-testid="del-reason" rows={2} value={delReason} onChange={(e) => setDelReason(e.target.value)} placeholder="Mis. salah input nominal / order ganda" /></div>
              <label className="flex items-start gap-2 cursor-pointer text-xs" data-testid="del-confirm-check">
                <input type="checkbox" checked={delConfirm} onChange={(e) => setDelConfirm(e.target.checked)} className="h-4 w-4 accent-rose-600 mt-0.5" />
                <span>Saya paham ini menghapus order & mengoreksi saldo secara permanen dari data operasional.</span>
              </label>
            </div>
          ) : <p className="text-sm text-muted-foreground py-4 text-center">Memuat rincian dampak...</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)}>Batal</Button>
            <Button variant="destructive" onClick={submitDelete} disabled={!delImpact || !delConfirm || delBusy} data-testid="del-submit">
              {isApprover ? "Hapus Order Sekarang" : "Kirim Permintaan Hapus"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
